import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'

import { discussionCsApi, DiscussionCsApiError } from './chapterDiscussions.api'
import {
  buildContentActionRequest,
  isDestructiveContentAction,
} from './chapterDiscussions.model'
import { DiscussionActionDialog } from './DiscussionActionDialog'
import { DiscussionUserEnforcementPanel } from './DiscussionUserEnforcementPanel'

import type {
  ChapterCommentReport,
  DiscussionCommentDetail,
  DiscussionContentActionRequest,
  DiscussionCsCommentIdentity,
  DiscussionReportActionRequest,
  SerializedTimestamp,
} from './chapterDiscussions.types'

export interface DiscussionModerationShowProps {
  identity: DiscussionCsCommentIdentity
}

function timestampText(value: SerializedTimestamp | string | undefined) {
  if (!value) return '—'
  if (typeof value === 'string') return new Date(value).toLocaleString()
  const seconds = value._seconds ?? value.seconds
  return seconds === undefined ? '—' : new Date(seconds * 1000).toLocaleString()
}

function EvidenceSection({ detail }: { detail: DiscussionCommentDetail }) {
  const runs = detail.moderationRuns ?? []
  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography variant="h6" gutterBottom>
        Immutable revision evidence
      </Typography>
      {runs.length === 0 ? (
        <Typography color="text.secondary">No moderation runs.</Typography>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Revision</TableCell>
              <TableCell>Decision</TableCell>
              <TableCell>Policy / model</TableCell>
              <TableCell>Entitlement</TableCell>
              <TableCell>Reasons</TableCell>
              <TableCell>Body snapshot</TableCell>
              <TableCell>Completed</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {runs.map((run, index) => (
              <TableRow key={run.runId ?? `${run.contentRevision}:${index}`}>
                <TableCell>{run.contentRevision}</TableCell>
                <TableCell>{run.decision ?? '—'}</TableCell>
                <TableCell>
                  {run.policyVersion ?? '—'}
                  <br />
                  {run.provider && `${run.provider} / ${run.model ?? '—'}`}
                </TableCell>
                <TableCell>
                  {run.entitlementEligible === undefined
                    ? '—'
                    : run.entitlementEligible
                      ? `eligible · ${run.entitlementReason ?? ''}`
                      : `ineligible · ${run.entitlementReason ?? ''}`}
                </TableCell>
                <TableCell>{run.reasonCodes?.join(', ') || '—'}</TableCell>
                <TableCell sx={{ maxWidth: 360, whiteSpace: 'pre-wrap' }}>
                  {run.bodySnapshot ?? '—'}
                </TableCell>
                <TableCell>{timestampText(run.completedAt)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Paper>
  )
}

export function DiscussionModerationShow({
  identity,
}: DiscussionModerationShowProps) {
  const [detail, setDetail] = useState<DiscussionCommentDetail | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [staleState, setStaleState] = useState<Record<string, unknown> | null>(
    null,
  )
  const [contentAction, setContentAction] = useState<
    DiscussionContentActionRequest['action'] | null
  >(null)
  const [reportAction, setReportAction] = useState<{
    report: ChapterCommentReport
    action: DiscussionReportActionRequest['action']
  } | null>(null)
  const [pending, setPending] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const response = await discussionCsApi.getComment(identity)
      setDetail(response)
      setError(null)
    } catch (requestError) {
      setError(
        requestError instanceof Error
          ? requestError.message
          : 'Unable to load discussion detail',
      )
    } finally {
      setLoading(false)
    }
  }, [identity])

  useEffect(() => {
    load()
    const refresh = () => load()
    window.addEventListener('discussion-detail-refresh', refresh)
    return () =>
      window.removeEventListener('discussion-detail-refresh', refresh)
  }, [load])

  const policyVersion = useMemo(() => {
    const runs = detail?.moderationRuns ?? []
    return (
      [...runs].reverse().find(run => run.policyVersion)?.policyVersion ??
      detail?.latestModerationRun?.policyVersion ??
      'vi-discussion-v1'
    )
  }, [detail])

  const handleApiError = async (requestError: unknown) => {
    const apiError = requestError as DiscussionCsApiError
    setError(apiError.message)
    if (apiError.status === 409 && apiError.errorCode === 'stale_state') {
      setStaleState(apiError.current ?? {})
      setContentAction(null)
      setReportAction(null)
      await load()
    }
  }

  const submitContentAction = async (reason: string) => {
    if (!detail || !contentAction) return
    setPending(true)
    try {
      await discussionCsApi.createContentAction(
        identity,
        buildContentActionRequest({
          action: contentAction,
          reason,
          policyVersion,
          comment: detail.comment,
        }),
      )
      setContentAction(null)
      await load()
    } catch (requestError) {
      await handleApiError(requestError)
    } finally {
      setPending(false)
    }
  }

  const submitReportAction = async (reason: string) => {
    if (!reportAction) return
    setPending(true)
    try {
      await discussionCsApi.createReportAction(
        identity,
        reportAction.report.reportId,
        {
          action: reportAction.action,
          reason,
          expectedStatus: reportAction.report.status,
        },
      )
      setReportAction(null)
      await load()
    } catch (requestError) {
      await handleApiError(requestError)
    } finally {
      setPending(false)
    }
  }

  if (loading && !detail) {
    return (
      <Stack alignItems="center" sx={{ p: 6 }}>
        <CircularProgress />
      </Stack>
    )
  }
  if (!detail) {
    return (
      <Alert severity="error">
        {error ?? 'Discussion comment could not be loaded.'}
      </Alert>
    )
  }

  return (
    <Box sx={{ p: 2 }}>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        justifyContent="space-between"
        spacing={2}
        sx={{ mb: 2 }}>
        <Box>
          <Typography variant="h5">Discussion moderation detail</Typography>
          <Typography variant="body2" color="text.secondary">
            {identity.mediaType} · book {identity.bookId} · chapter{' '}
            {identity.chapterId} · {identity.commentId}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} flexWrap="wrap">
          {(
            ['approve', 'hide', 'restore', 'reevaluate', 'delete'] as const
          ).map(action => (
            <Button
              key={action}
              variant="outlined"
              color={action === 'delete' ? 'error' : 'primary'}
              disabled={pending}
              onClick={() => setContentAction(action)}>
              {action}
            </Button>
          ))}
        </Stack>
      </Stack>
      {staleState && (
        <Alert
          severity="warning"
          sx={{ mb: 2 }}
          onClose={() => setStaleState(null)}>
          The discussion changed while the action was open. The draft was
          discarded and current data was reloaded.
          {Object.keys(staleState).length > 0 &&
            ` Current state: ${JSON.stringify(staleState)}`}
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      <Stack spacing={2}>
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
            <Chip label={detail.comment.moderationStatus} size="small" />
            <Chip label={detail.comment.visibilityStatus} size="small" />
            <Chip
              label={`revision ${detail.comment.contentRevision}`}
              size="small"
            />
          </Stack>
          <Typography sx={{ whiteSpace: 'pre-wrap' }}>
            {detail.comment.body}
          </Typography>
          <Divider sx={{ my: 2 }} />
          <Typography variant="body2" color="text.secondary">
            Author {detail.comment.authorId} · updated{' '}
            {timestampText(detail.comment.updatedAt)}
          </Typography>
        </Paper>

        {detail.parentComment && (
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="h6">Reviewed parent context</Typography>
            <Typography variant="body2" color="text.secondary">
              Parent {detail.parentComment.commentId} · reviewed revision{' '}
              {detail.comment.moderatedParentRevision ?? '—'}
            </Typography>
            <Typography sx={{ mt: 1, whiteSpace: 'pre-wrap' }}>
              {detail.parentEvidence?.bodySnapshot ?? detail.parentComment.body}
            </Typography>
          </Paper>
        )}

        <EvidenceSection detail={detail} />

        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom>
            CS content action history
          </Typography>
          {(detail.moderationActions ?? []).length === 0 ? (
            <Typography color="text.secondary">No CS actions.</Typography>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Action</TableCell>
                  <TableCell>Actor</TableCell>
                  <TableCell>Revision</TableCell>
                  <TableCell>Reason</TableCell>
                  <TableCell>Time</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {detail.moderationActions?.map((action, index) => (
                  <TableRow key={action.actionId ?? index}>
                    <TableCell>{action.action}</TableCell>
                    <TableCell>{action.actorId}</TableCell>
                    <TableCell>{action.contentRevision}</TableCell>
                    <TableCell>{action.reason}</TableCell>
                    <TableCell>{timestampText(action.createdAt)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Paper>

        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom>
            Reports
          </Typography>
          {detail.reports.length === 0 ? (
            <Typography color="text.secondary">No reports.</Typography>
          ) : (
            <Stack spacing={2}>
              {detail.reports.map(report => (
                <Paper key={report.reportId} variant="outlined" sx={{ p: 2 }}>
                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    justifyContent="space-between"
                    spacing={2}>
                    <Box>
                      <Typography variant="subtitle2">
                        {report.reportId} · revision{' '}
                        {report.reportedContentRevision}
                      </Typography>
                      <Typography variant="body2">
                        {report.reason} · reporter {report.reporterUserId}
                      </Typography>
                      <Typography sx={{ mt: 1, whiteSpace: 'pre-wrap' }}>
                        {report.reportedBodySnapshot ?? 'Evidence unavailable'}
                      </Typography>
                    </Box>
                    <Stack direction="row" spacing={1} alignItems="flex-start">
                      <Chip size="small" label={report.status} />
                      {report.status === 'open' && (
                        <>
                          <Button
                            size="small"
                            onClick={() =>
                              setReportAction({ report, action: 'resolve' })
                            }>
                            Resolve
                          </Button>
                          <Button
                            size="small"
                            onClick={() =>
                              setReportAction({ report, action: 'dismiss' })
                            }>
                            Dismiss
                          </Button>
                        </>
                      )}
                    </Stack>
                  </Stack>
                  {(detail.reportActions?.[report.reportId] ?? []).map(
                    (action, index) => (
                      <Typography
                        key={action.actionId ?? index}
                        variant="caption"
                        display="block"
                        color="text.secondary"
                        sx={{ mt: 1 }}>
                        {action.action} by {action.actorId}: {action.reason} ·{' '}
                        {timestampText(action.createdAt)}
                      </Typography>
                    ),
                  )}
                </Paper>
              ))}
            </Stack>
          )}
        </Paper>

        <DiscussionUserEnforcementPanel
          userId={detail.comment.authorId}
          enforcement={detail.enforcement}
        />

        {(detail.enforcementActions?.length ?? 0) > 0 && (
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="h6">Enforcement history</Typography>
            {detail.enforcementActions?.map((action, index) => (
              <Typography
                key={action.actionId ?? index}
                variant="body2"
                sx={{ mt: 1 }}>
                Generation {action.generation}: {action.action} by{' '}
                {action.actorId} — {action.reason} ·{' '}
                {timestampText(action.createdAt)}
              </Typography>
            ))}
          </Paper>
        )}

        {(detail.bulkDeleteJobs?.length ?? 0) > 0 && (
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="h6">Delete-all history</Typography>
            {detail.bulkDeleteJobs?.map(job => (
              <Typography key={job.jobId} variant="body2" sx={{ mt: 1 }}>
                {job.jobId}: {job.state} · scanned {job.scannedCount} · deleted{' '}
                {job.deletedCount} · failed {job.failedCount}
              </Typography>
            ))}
          </Paper>
        )}
      </Stack>

      <DiscussionActionDialog
        open={contentAction !== null}
        title={`${contentAction ?? ''} comment`}
        description={`Apply ${contentAction ?? ''} to the exact revision currently displayed.`}
        confirmLabel={contentAction ?? 'Confirm'}
        destructive={
          contentAction ? isDestructiveContentAction(contentAction) : false
        }
        pending={pending}
        error={error}
        onClose={() => setContentAction(null)}
        onConfirm={submitContentAction}
      />
      <DiscussionActionDialog
        open={reportAction !== null}
        title={`${reportAction?.action ?? ''} report`}
        description="This changes only the report workflow state. It does not change comment visibility."
        confirmLabel={reportAction?.action ?? 'Confirm'}
        pending={pending}
        error={error}
        onClose={() => setReportAction(null)}
        onConfirm={submitReportAction}
      />
    </Box>
  )
}
